'use client';

import { useState, useRef, useEffect } from 'react';
import styles from './GlobalCollapsibleButton.module.css';

interface GlobalCollapsibleButtonProps {
  title: string;
  children: React.ReactNode;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
}

export default function GlobalCollapsibleButton({ 
  title, 
  children, 
  isOpen = false, 
  onToggle,
  className = '' 
}: GlobalCollapsibleButtonProps) {
  const [isExpanded, setIsExpanded] = useState(isOpen);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [children]);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  return (
    <div className={`${styles.collapsibleContainer} ${className}`}>
      <button 
        className={styles.collapsibleButton}
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        <span className={styles.buttonTitle}>{title}</span>
        <div className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path 
              d="M3 4.5L6 7.5L9 4.5" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>
      
      <div 
        className={`${styles.collapsibleContent} ${isExpanded ? styles.contentOpen : ''}`}
        style={{ height: isExpanded ? `${contentHeight}px` : '0px' }}
      >
        <div ref={contentRef} className={styles.contentInner}>
          {children}
        </div>
      </div>
    </div>
  );
}
